import AppContainer from 'app_components/layouts/app'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import './TutorialsLandingAdmin.scss'
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import { useTranslation } from 'react-i18next';
import ModalTutorialForm from './components/ModalTutorialForm/ModalTutorialForm';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { IoIosAdd } from 'react-icons/io';
import { ILandingTutorial } from 'app_types/admin/landings/landingTypes';
import LandingService from 'app_services/admin/landings/landingService';
import AlertComponent from 'app_components/commons/alert/containers/alertComponent';
import TutorialAdminCard from './components/TutorialAdminCard/TutorialAdminCard';
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import TermListModule from 'app_components/modules/TermListModule/TermListModule';
import { Terms, ITerm } from 'app_types/admin/term/term.types';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';

const LANDING_SLUG = 'tutorials'

const TutorialsLandingAdmin: FC = () => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [tutorials, setTutorials] = useState<ILandingTutorial[]>([])
  const landingService = useMemo(() => new LandingService(), [])
  const screenNotification = useMemo(() => new ScreenNotificationsUtil(), [])
  const [editData, setEditData] = useState<ILandingTutorial | undefined>(undefined)
  const [publicGroups, setPublicGroups] = useState<ITerm[]>([])

  const getLandingData = useCallback(async () => {
    const landingResponse = await landingService.fetchLandingData({
      slug: LANDING_SLUG
    })
    if (landingResponse && landingResponse?.landing?.tutorials) {
      setTutorials(landingResponse?.landing?.tutorials)
    }
  }, [landingService])

  useEffect(() => {
    getLandingData()
  }, [getLandingData])

  const handleCloseModal = useCallback((saved?: boolean) => {
    setIsModalOpen(false)
    if (saved) {
      getLandingData()
      screenNotification.showSuccessTimer()
    }
    setEditData(undefined)
  }, [getLandingData, screenNotification])

  const handleClickOpenModal = (newEditData?: ILandingTutorial) => {
    setIsModalOpen(true)
    setEditData(newEditData)
  }

  const handleClickDelete = useCallback(async (unique: string) => {
    const confirm = await screenNotification.showAskDelete()
    if (confirm.isConfirmed) {
      const response = await landingService.removeTutorial(LANDING_SLUG, unique)
      if (response) {
        getLandingData()
        screenNotification.showSuccessTimer()
      }
    }
  }, [getLandingData, landingService, screenNotification])

  const handleChangePublicGroups = useCallback((newGroups: ITerm[]) => {
    setPublicGroups([...newGroups])
    getLandingData()
  }, [getLandingData])

  const handleDragEnd = async ({ source, destination }: DropResult) => {
    if (!source || !destination) return
    const newItems = [...tutorials]
    const [removed] = newItems.splice(source.index, 1)
    newItems.splice(destination.index, 0, removed)
    const sortedItems = newItems.map((item, idx) => ({...item, position: idx}))
    setTutorials(sortedItems)
    await landingService.updateTutorialPositions({ tutorials: sortedItems, slug: LANDING_SLUG })
    getLandingData()
  }

  const tutorialsToRender = tutorials?.sort((a, b) => Number(a.position) - Number(b.position))

  return (
    <AppContainer>
      <div className='tutorials-landing-admin--layout'>
        <BreadcrumbComponent links={['/admin/landings', '#']} texts={[`${t('components.breadcrumb.landings')}`, `${t('components.breadcrumb.tutorials')}`]} />

        <div className="tutorials-landing-admin--content">
          <div>
            <ButtonComponent
              variant="primary"
              onClick={() => handleClickOpenModal()}
            >
              <ButtonComponent.Icon>
                <IoIosAdd />
              </ButtonComponent.Icon>
              Agregar tutorial
            </ButtonComponent>
            <DragDropContext
              onDragEnd={handleDragEnd}
            >
              <Droppable
                droppableId='tutorials-landing-list-droppable'
              >
                {({ innerRef, droppableProps }) => (
                  <div
                    ref={innerRef}
                    {...droppableProps}
                    className="tutorials-landing-admin--list"
                  >
                    {tutorialsToRender?.length ? (
                      tutorialsToRender.map((tutorial, idx) => (
                        <Draggable
                          key={tutorial.unique}
                          draggableId={tutorial.unique}
                          index={idx}
                        >
                          {(provided) => (
                            <TutorialAdminCard
                              tutorial={tutorial}
                              provided={provided}
                              onEdit={() => handleClickOpenModal(tutorial)}
                              onDelete={() => handleClickDelete(tutorial.unique)}
                            />
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <AlertComponent  />
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div>
            <TermListModule
              description='Grupos públicos'
              newButtonText='Nuevo grupo'
              type={Terms.TUTORIAL_CATEGORY}
              onChangeItems={handleChangePublicGroups}
            />
          </div>
        </div>
        <ModalTutorialForm
          onClose={handleCloseModal}
          open={isModalOpen}
          editData={editData}
          publicGroups={publicGroups}
          slug="tutorials"
        />
      </div>
    </AppContainer>
  )
}

export default TutorialsLandingAdmin
